import React from "react";
import { createPortal } from "react-dom";
import { LuLoader } from "react-icons/lu";

type PopConfirmProps = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | boolean | Promise<void | boolean>;
  children: React.ReactElement<{
    onClick?: React.MouseEventHandler;
    "aria-haspopup"?: string;
  }>;
};

const PopConfirm: React.FC<PopConfirmProps> = ({
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  children,
}) => {
  const [open, setOpen] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!open) return;

    cancelButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !confirming) setOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [confirming, open]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const result = await onConfirm();
      if (result !== false) setOpen(false);
    } finally {
      setConfirming(false);
    }
  };

  const handleTriggerClick: React.MouseEventHandler = (event) => {
    children.props.onClick?.(event);
    setOpen(true);
  };

  const trigger = React.cloneElement(children, {
    "aria-haspopup": "dialog",
    onClick: handleTriggerClick,
  });

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !confirming) {
                setOpen(false);
              }
            }}
          >
            <div
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="pop-confirm-title"
              aria-describedby={
                description ? "pop-confirm-description" : undefined
              }
              className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
            >
              <h2
                id="pop-confirm-title"
                className="text-lg font-semibold text-gray-900"
              >
                {title}
              </h2>
              {description && (
                <p
                  id="pop-confirm-description"
                  className="mt-2 text-sm text-gray-600"
                >
                  {description}
                </p>
              )}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  ref={cancelButtonRef}
                  type="button"
                  disabled={confirming}
                  onClick={() => setOpen(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {cancelText}
                </button>
                <button
                  type="button"
                  disabled={confirming}
                  onClick={handleConfirm}
                  className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {confirming && (
                    <LuLoader
                      size={16}
                      className="mr-2 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  {confirmText}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default PopConfirm;
